// Step 7 of the campaign builder: the review and pre-flight before publishing,
// or the status of a campaign that has published.

import { CampaignStatusSection } from '@/components/campaign-status-section';
import { Icons, fmt } from '@/components/primitives';
import { campaignIsOver, campaignStatusLabel } from '@/lib/campaign-status';
import { MetaDeliveryPolicy } from '@/components/meta-delivery-policy';
import { Section } from '@/components/wizard-section';
import { CHANNEL_FORMATS } from './creative-config';
import { fmtDay, fmtDayShort } from '@/lib/datetime';
import { providerLabel } from './create-publishing-modal';
import type { CreateFlowModel } from './create-flow-model';

export function ReviewStep({ model }: { model: CreateFlowModel }) {
  const { plan, setView, availableCreatives, status, wasLive, production, channel,
    metaContract, budget, startDate, endDate, audienceSlots, markup, flights, open, toggle,
    savedCampaignId, preflightQuery, audSize, audienceTypeSummary, audSizeUnavailableReason,
    campaignSyncQuery, publishTarget, overviewQuery, creativeSelectionCount, preflight, statusLine,
    headerStatusKind, saving, handleSaveDraft, handlePrepareReady, handleDuplicate, operationalStatus,
  } = model;
  // The summary's first item is the header's own headline ("Ended Oct 18 on
  // Meta"), and the facts below say what ran once nothing more will.
  const over = campaignIsOver(operationalStatus);
  return (
    <Section
      n="7"
      title={wasLive ? 'Status' : 'Review & Ready to Publish'}
      summary={wasLive
        ? [
            (() => {
              if (statusLine) return statusLine.headline;
              const label = campaignStatusLabel(headerStatusKind);
              return `${label.charAt(0).toUpperCase()}${label.slice(1)} on ${providerLabel(publishTarget)}`;
            })(),
            plan?.revision ? `revision ${plan.revision}` : null,
            preflight ? (preflight.ready ? 'every check passing' : 'a check needs attention') : null,
          ].filter(Boolean).join(' · ')
        : 'Validate the local plan before provider handoff'}
      open={open.rev}
      done={status === 'ready_to_publish' || wasLive}
      onToggle={()=>toggle('rev')}
    >
      {wasLive ? (
        <CampaignStatusSection
          target={publishTarget}
          revision={plan?.revision}
          sync={campaignSyncQuery.data}
          preflight={preflight}
          preflightPending={preflightQuery.isPending}
          preflightError={preflightQuery.isError ? (preflightQuery.error?.message || 'Checks could not be loaded.') : undefined}
          deliveryCutoff={overviewQuery.data?.delivery_cutoff ?? null}
          finished={over}
          creativeName={(creativeId) => availableCreatives.find((row) => row.id === creativeId)?.name ?? 'this creative'}
          onDuplicate={() => { void handleDuplicate(); }}
          onBack={() => setView('dashboard')}
        />
      ) : (
      <>
      <MetaDeliveryPolicy
        contract={metaContract}
        variant="review"
        heading="Review all enforced Meta settings"
      />
      <div className="grid-2 mb-3">
        <div>
          {[
            ['Production', production?.title || '—'],
            ['Channel', channel ? CHANNEL_FORMATS[channel].name : '—'],
            ['Audience', `${audienceTypeSummary} · ${audienceSlots.length} reportable audience${audienceSlots.length === 1 ? '' : 's'} · ${audSize !== null ? `${audSize.toLocaleString()} matched` : (audSizeUnavailableReason || 'size pending')}`],
            ['Creative', creativeSelectionCount ? `${creativeSelectionCount} campaign creative${creativeSelectionCount === 1 ? '' : 's'} selected` : 'No creative selected'],
            ['Budget (gross)', fmt.$full(budget)],
            ['Agency margin', markup ? (channel === 'meta' ? '15% of gross' : channel === 'ctv' ? '$25 → $30 reported CPM' : channel === 'dsp' ? '$6 → $8 reported CPM' : 'On') : 'Off'],
            ['Start date', fmtDay(startDate)],
            ['End date', fmtDay(endDate)],
          ].map(([k,v]) => (
            <div key={k} style={{display:'grid',gridTemplateColumns:'120px 1fr',padding:'7px 0',borderBottom:'1px solid var(--line-2)',fontSize:12}}>
              <span className="muted" style={{fontFamily:'var(--font-mono)',fontSize:10.5,letterSpacing:'.08em',textTransform:'uppercase'}}>{k}</span>
              <b style={{color:'var(--ink)'}}>{v}</b>
            </div>
          ))}
          {flights.length > 1 && (
            <div style={{padding:'10px 0 2px',borderBottom:'1px solid var(--line-2)'}}>
              <div style={{display:'grid',gridTemplateColumns:'120px 1fr',fontSize:12,alignItems:'start'}}>
                <span className="muted" style={{fontFamily:'var(--font-mono)',fontSize:10.5,letterSpacing:'.08em',textTransform:'uppercase',paddingTop:2}}>Flights · {flights.length}</span>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {[...flights].sort((a,b)=>new Date(a.start).getTime()-new Date(b.start).getTime()).map((f, i) => {
                    const color = `oklch(${0.65 - i*0.05} 0.13 ${(i * 60 + 30) % 360})`;
                    return (
                      <div key={f.id} style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                        <span style={{width:6,height:18,borderRadius:2,background:color,flexShrink:0}}></span>
                        <b style={{color:'var(--ink)',flex:'0 0 auto',minWidth:80}}>{f.name}</b>
                        {/* No year: the start and end dates above carry it, and with it the dates wrapped (F5 UX P7). */}
                        <span className="muted mono" style={{fontSize:11}}>{fmtDayShort(f.start)} → {fmtDayShort(f.end)}</span>
                        <span style={{marginLeft:'auto',color:'var(--ink)',fontVariantNumeric:'tabular-nums'}}>{fmt.$full(+f.budget||0)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="field-lbl mb-2">Pre-flight checks</div>
          {!savedCampaignId && (
            <div className="muted" style={{fontSize:11.5,lineHeight:1.5}}>
              Save the draft to run the pre-flight checks.
            </div>
          )}
          {savedCampaignId && preflightQuery.isPending && !preflight && (
            <div className="muted" style={{fontSize:11.5}}>Checking the saved campaign plan…</div>
          )}
          {savedCampaignId && preflightQuery.isError && !preflight && (
            <div style={{fontSize:11.5,color:'var(--bad)'}}>
              {preflightQuery.error?.message || 'Pre-flight could not be loaded.'}
            </div>
          )}
          {preflight?.checks?.map((check, index) => {
            const ok = check.status === 'pass';
            const segmentLabel = check.segment_position === undefined
              ? ''
              : `Audience ${check.segment_position + 1}: `;
            return (
              <div key={`${check.code}-${check.segment_position ?? 'campaign'}-${index}`} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'6px 0',fontSize:12}}>
                <span style={{color: ok ? 'var(--good)' : 'var(--bad)', display:'inline-flex',marginTop:1}}>{ok ? Icons.check : Icons.bell}</span>
                <span><b>{segmentLabel}</b>{check.message}</span>
              </div>
            );
          })}
          {preflight && (
            <div
              className="muted"
              style={{
                marginTop: 8,
                fontSize: 11,
                color: preflight.ready ? 'var(--good)' : 'var(--ink-2)',
              }}
            >
              Revision {preflight.revision} · {preflight.ready
                ? 'ready for publishing handoff'
                : 'not ready — resolve the failed checks above'}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3" style={{borderTop:'1px solid var(--line-2)',paddingTop:14,display:'flex',gap:8}}>
        <button
          className="btn"
          style={{flex:1,justifyContent:'center',padding:'10px'}}
          disabled={saving}
          onClick={() => { void handleSaveDraft(); }}
        >
          {saving ? 'Saving…' : 'Save Draft'}
        </button>
        <button
          className="btn btn-primary"
          style={{flex:1,justifyContent:'center',padding:'10px'}}
          disabled={saving}
          onClick={() => { void handlePrepareReady(); }}
        >
          {Icons.play} {preflight?.ready ? 'Mark Ready to Publish' : 'Save & Run Pre-flight'}
        </button>
      </div>
      </>
      )}
    </Section>
  );
}
